/**
 * Drawdown Service - Backend API Integration
 *
 * Service for the new stateless drawdown API endpoints.
 * Replaces frontend calculation logic with backend-driven approach.
 *
 * Note: Prepayment pool handling is automatic based on fund settings:
 * - First drawdown: Applies fund.minimum_prepayment per investor if set
 * - Subsequent drawdowns: Auto-consumes from prepayment pools
 */

import { apiClient } from '../../utils/api-client';

// Types matching the backend schemas
export interface ManualOverride {
  investor_id: string;
  field: 'allocation_percentage' | 'gross_amount' | 'prepayment_used';
  value: number;
}

export interface DrawdownCalculateRequest {
  call_type: 'percentage' | 'fixed_amount';
  input_value: number;
  currency: string;
  issue_date: string;
  manual_overrides: ManualOverride[];
}

export interface InvestorAllocation {
  investor_id: string;
  fund_relationship_id: string;
  investor_name: string;
  commitment_amount: number;
  allocation_percentage: number;
  gross_amount: number;
  prepayment_used: number;
  prepayment_pool_balance: number;
  net_amount_due: number;
  percentage_of_total_call: number;
  prepayment_contribution: number;
  is_manual_override: boolean;
  calculation_notes: string;
}

export interface DrawdownCalculationResult {
  total_gross_amount: number;
  total_net_amount: number;
  total_prepayment_used: number;
  total_prepayment_contribution: number;
  allocations: InvestorAllocation[];
  is_valid: boolean;
  errors: string[];
  warnings: string[];
  calculation_hash: string;
  // Prepayment pool info (for UI display)
  is_first_drawdown: boolean;
  fund_minimum_prepayment_pool: number | null;
}

export type PreCalculatedInvestorAllocation = Pick<
  InvestorAllocation,
  | 'investor_id'
  | 'fund_relationship_id'
  | 'allocation_percentage'
  | 'gross_amount'
  | 'prepayment_used'
  | 'net_amount_due'
  | 'prepayment_contribution'
  | 'is_manual_override'
> & {
  override_reason?: string;
};

export interface DrawdownCreateRequest {
  call_type: 'percentage' | 'fixed_amount';
  input_value: number;
  currency: string;
  issue_date: string;
  due_date: string;
  purpose: string;
  description?: string;
  allocations: PreCalculatedInvestorAllocation[];
  calculation_hash: string;
}

export interface DrawdownCreateResponse {
  capital_call_id: string;
  drawdown_number: number;
  status: string;
  total_amount: number;
  total_net_amount: number;
  investor_count: number;
  created_at: string;
}

// Pydantic serializes Decimal as strings; coerce numeric fields so arithmetic
// and strict comparisons (=== 0, <) behave correctly. Booleans/strings pass through.
function normalizeAllocation(
  allocation: InvestorAllocation
): InvestorAllocation {
  return {
    ...allocation,
    commitment_amount: Number(allocation.commitment_amount),
    allocation_percentage: Number(allocation.allocation_percentage),
    gross_amount: Number(allocation.gross_amount),
    prepayment_used: Number(allocation.prepayment_used),
    prepayment_pool_balance: Number(allocation.prepayment_pool_balance ?? 0),
    net_amount_due: Number(allocation.net_amount_due),
    percentage_of_total_call: Number(allocation.percentage_of_total_call),
    prepayment_contribution: Number(allocation.prepayment_contribution),
  };
}

function normalizeDrawdownCalculationResult(
  result: DrawdownCalculationResult
): DrawdownCalculationResult {
  return {
    ...result,
    total_gross_amount: Number(result.total_gross_amount),
    total_net_amount: Number(result.total_net_amount),
    total_prepayment_used: Number(result.total_prepayment_used),
    total_prepayment_contribution: Number(result.total_prepayment_contribution),
    fund_minimum_prepayment_pool:
      result.fund_minimum_prepayment_pool === null
        ? null
        : Number(result.fund_minimum_prepayment_pool),
    allocations: result.allocations.map(normalizeAllocation),
  };
}

export class DrawdownCalculationService {
  /**
   * Calculate drawdown allocations with optional manual overrides
   */
  static async calculateDrawdown(
    fundId: string,
    request: DrawdownCalculateRequest
  ): Promise<DrawdownCalculationResult> {
    const response = await apiClient.post<DrawdownCalculationResult>(
      `/funds/${fundId}/drawdowns/calculate`,
      request
    );
    return normalizeDrawdownCalculationResult(response);
  }

  /**
   * Create drawdown with pre-calculated allocations
   */
  static async createDrawdown(
    fundId: string,
    request: DrawdownCreateRequest
  ): Promise<DrawdownCreateResponse> {
    const response = await apiClient.post<DrawdownCreateResponse>(
      `/funds/${fundId}/drawdowns`,
      request
    );
    return response;
  }
}
